import {
  DbDomainArg,
  DbExtension,
  GeneratorIgnore,
  IsQuery,
  Query,
  SelectableFromShape,
} from './query';
import { QueryMethods } from './query-methods';
import {
  anyShape,
  Column,
  ColumnSchemaConfig,
  ColumnsShape,
  DefaultColumnTypes,
  getColumnTypes,
  makeColumnTypes,
} from '../columns';
import { inspect } from 'node:util';
import { AsyncLocalStorage } from 'node:async_hooks';
import {
  DynamicRawSQL,
  raw,
  RawSql,
  sqlFn,
  UnsafeSqlExpression,
} from './expressions/raw-sql';
import { SqlRefExpression } from './expressions/sql-ref-expression';
import { ScopeArgumentQuery } from './extra-features/scope/scope';
import {
  defaultSchemaConfig,
  DefaultSchemaConfig,
} from '../columns/default-schema-config';
import {
  enableSoftDelete,
  NonDeletedScope,
  SoftDeleteOption,
} from './basic-features/mutate/soft-delete';
import {
  parseTableData,
  TableData,
  TableDataFn,
  TableDataItem,
  TableDataItemsUniqueColumns,
  TableDataItemsUniqueColumnTuples,
  TableDataItemsUniqueConstraints,
  UniqueQueryTypeOrExpression,
} from '../tableData';
import {
  applyComputedColumns,
  ComputedColumnsFromOptions,
  ComputedOptionsFactory,
} from './extra-features/computed/computed';
import { DbSqlQuery, performQuery, SQLQueryArgs } from './db-sql-query';
import { setDb } from '../columns/operators';
import {
  applyMixins,
  emptyObject,
  EmptyObject,
  MaybeArray,
  pushOrNewArray,
  RecordString,
  RecordUnknown,
  toSnakeCase,
} from '../utils';
import { Adapter, QueryArraysResult } from '../adapters/adapter';
import { NotFoundError, QueryError, QueryErrorName } from './errors';
import { ColumnsParsers } from './query-columns/query-column-parsers';
import { DynamicSQLArg, StaticSQLArgs } from './expressions/expression';
import { logParamToLogObject, QueryLogOptions } from './basic-features/log/log';
import {
  handleResult,
  QueryCatch,
  QueryThenShallowSimplifyArr,
} from './then/then';
import {
  _queryHookBeforeCreate,
  _queryHookBeforeSave,
  _queryHookBeforeUpdate,
} from './extra-features/hooks/hooks';
import { QueryData, QueryDataScopes } from './query-data';
import { QueryInternal } from './query-internal';
import { QuerySchema } from './basic-features/schema/schema';
import { AsyncState } from './basic-features/storage/storage';
import { DbRole } from './extra-features/roles/roles';
import { DbRlsOptions } from './extra-features/rls/rls.db';

export type ShapeColumnPrimaryKeys<Shape extends Column.QueryColumnsInit> = {
  [K in {
    [K in keyof Shape]: Shape[K]['data']['primaryKey'] extends string
      ? K
      : never;
  }[keyof Shape]]: UniqueQueryTypeOrExpression<Shape[K]['queryType']>;
};

export type ShapeUniqueColumns<Shape extends Column.QueryColumnsInit> = {
  [K in keyof Shape]: Shape[K]['data']['unique'] extends string
    ? {
        [C in K]: UniqueQueryTypeOrExpression<Shape[K]['queryType']>;
      }
    : never;
}[keyof Shape];

export type UniqueConstraints<Shape extends Column.QueryColumnsInit> = {
  [K in keyof Shape]: Shape[K]['data']['primaryKey'] extends string
    ? string extends Shape[K]['data']['primaryKey']
      ? never
      : Shape[K]['data']['primaryKey']
    : Shape[K]['data']['unique'] extends string
      ? string extends Shape[K]['data']['unique']
        ? never
        : Shape[K]['data']['unique']
      : never;
}[keyof Shape];

export type NoPrimaryKeyOption = 'error' | 'warning' | 'ignore';

// Options that are also available in `orchidORM` of the ORM
export interface DbSharedOptions extends QueryLogOptions {
  autoPreparedStatements?: boolean;
  noPrimaryKey?: NoPrimaryKeyOption;
  rls?: DbRlsOptions;
  extensions?: (string | RecordString)[];
  domains?: {
    [K: string]: DbDomainArg<DefaultColumnTypes<DefaultSchemaConfig>>;
  };
  generatorIgnore?: GeneratorIgnore;
  schema?: QuerySchema;
  /**
   * For `hasMany` and `hasOne`:
   * controls nested create|connect|connectOrCreate strategy in `createMany`.
   * When creating many records <= this value, it will use a single query with CTEs.
   * Otherwise, it will perform nested operations in separate queries in a transaction.
   * A single query is more efficient on lower amount of records and on lower latency to a database.
   */
  nestedCreateBatchMax?: number;
  roles?: DbRole[];
  managedRolesSql?: string;
}

export interface DbOptions<
  SchemaConfig extends ColumnSchemaConfig,
  ColumnTypes,
> extends DbSharedOptions {
  schemaConfig?: SchemaConfig;
  // concrete column types or a callback for overriding standard column types
  // this types will be used in tables to define their columns
  columnTypes?:
    | ColumnTypes
    | ((t: DefaultColumnTypes<SchemaConfig>) => ColumnTypes);
  // when set to true, all columns will be translated to `snake_case` when querying database
  snakeCase?: boolean;
  // if `now()` for some reason doesn't suite your timestamps, provide a custom SQL for it
  nowSQL?: string;
}

export interface DbOptionsWithAdapter<
  SchemaConfig extends ColumnSchemaConfig,
  ColumnTypes,
> extends DbOptions<SchemaConfig, ColumnTypes> {
  adapter: Adapter;
}

// Options of `createDb`.
export interface DbTableOptions<
  ColumnTypes,
  Table extends string | undefined,
  Shape extends Column.QueryColumns,
> extends QueryLogOptions {
  schema?: QuerySchema;
  /**
   * Prepare all SQL queries before executing,
   * true by default
   */
  autoPreparedStatements?: boolean;
  noPrimaryKey?: NoPrimaryKeyOption;
  snakeCase?: boolean;
  /**
   * Default language for the full text search
   */
  language?: string;
  /**
   * See {@link ScopeMethods}
   */
  scopes?: DbTableOptionScopes<Table, Shape>;
  /**
   * See {@link SoftDeleteMethods}
   */
  softDelete?: SoftDeleteOption<Shape>;
  /**
   * Table comment, for migrations generator
   */
  comment?: string;
  /**
   * Computed SQL or JS columns definitions
   */
  computed?: ComputedOptionsFactory<ColumnTypes, Shape>;
  /**
   * For customizing `now()` sql, used in soft delete
   */
  nowSQL?: string;
}

export type DbTableOptionScopes<
  Table extends string | undefined,
  Shape extends Column.QueryColumns,
  Keys extends string = string,
> = { [K in Keys]: (q: ScopeArgumentQuery<Table, Shape>) => IsQuery };

export interface QueryBuilder extends Query {
  returnType: undefined;
}

export class Db<
  Table extends string | undefined = undefined,
  Shape extends Column.QueryColumnsInit = Column.QueryColumnsInit,
  PrimaryKeys = never,
  UniqueColumns = never,
  UniqueColumnTuples = never,
  UniqueConstraints = never,
  ColumnTypes = DefaultColumnTypes<ColumnSchemaConfig>,
  ShapeWithComputed extends Column.QueryColumnsInit = Shape,
  Scopes extends RecordUnknown | undefined = EmptyObject,
  DefaultSelect extends keyof Shape = keyof Shape,
>
  extends QueryMethods<ColumnTypes>
  implements Query
{
  declare q: QueryData;
  declare __isQuery: true;
  declare __as: Table & string;
  declare __selectable: SelectableFromShape<ShapeWithComputed, Table>;
  // declare __subQuery: boolean;
  declare __hasSelect: boolean;
  declare __hasWhere: boolean;
  declare __defaults: {
    [K in {
      [K in keyof Shape]: unknown extends Shape[K]['data']['default']
        ? never
        : K;
    }[keyof Shape]]: true;
  };
  declare __scopes: { [K in keyof Scopes]: true };
  declare __defaultSelect: DefaultSelect;
  baseQuery: Query;
  columns: (keyof Shape)[];
  declare outputType: ColumnsShape.DefaultSelectOutput<Shape>;
  declare inputType: ColumnsShape.Input<Shape>;
  declare result: { [K in DefaultSelect]: Shape[K] };
  declare returnType: undefined;
  declare then: QueryThenShallowSimplifyArr<ColumnsShape.DefaultOutput<Shape>>;
  declare windows: EmptyObject;
  relations: EmptyObject;
  relationQueries: EmptyObject;
  declare withData: EmptyObject;
  error: new (
    message: string,
    length: number,
    name: QueryErrorName,
  ) => QueryError<this>;
  internal: QueryInternal<
    {
      [K in keyof PrimaryKeys]: (
        keyof PrimaryKeys extends K ? never : keyof PrimaryKeys
      ) extends never
        ? PrimaryKeys[K]
        : never;
    }[keyof PrimaryKeys],
    PrimaryKeys | UniqueColumns,
    | {
        [K in keyof Shape]: Shape[K]['data']['unique'] extends string
          ? K
          : never;
      }[keyof Shape]
    | keyof PrimaryKeys,
    UniqueColumnTuples,
    UniqueConstraints
  >;
  declare catch: QueryCatch;
  shape: ShapeWithComputed;

  constructor(
    public adapterNotInTransaction: Adapter,
    public qb: QueryBuilder,
    public table: Table = undefined as Table,
    shape: ShapeWithComputed,
    public columnTypes: ColumnTypes,
    asyncStorage: AsyncLocalStorage<AsyncState>,
    options: DbTableOptions<ColumnTypes, Table, ShapeWithComputed>,
    tableData: TableData = {},
  ) {
    super();

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;

    const { softDelete } = options;
    const scopes = (
      options.scopes || softDelete ? {} : emptyObject
    ) as QueryDataScopes;

    this.baseQuery = this as Query;
    this.relations = {};
    this.relationQueries = {};

    const logger = options.logger || console;

    const parsers = {} as ColumnsParsers;
    let hasParsers = false;
    let modifyQuery: ((q: Query) => void)[] | undefined = undefined;
    let prepareSelectAll = false;
    let hasHookSetters: true | undefined;
    let runtimeDefaultColumns: string[] | undefined;
    let selectAllCount = 0;
    const { snakeCase } = options;

    if (shape !== anyShape) {
      shape = { ...shape };
    }
    this.shape = shape;

    for (const key in shape) {
      const column = Object.create(shape[key]) as unknown as Column;
      shape[key] = column as never;
      column.data = { ...column.data };
      column.data.key = key;

      if (column._parse) {
        hasParsers = true;
        parsers[key] = column._parse;
      }

      if (column.data.name) {
        prepareSelectAll = true;
      } else if (snakeCase) {
        const snakeName = toSnakeCase(key);
        if (snakeName !== key) {
          prepareSelectAll = true;
          column.data.name = snakeName;
        }
      }

      if (column.data.explicitSelect) {
        prepareSelectAll = true;
      } else {
        selectAllCount++;
      }

      const { modifyQuery: mq } = column.data;
      if (mq) {
        modifyQuery = pushOrNewArray(modifyQuery, (q: Query) => mq(q, column));
      }

      if (typeof column.data.default === 'function') {
        if (!runtimeDefaultColumns) runtimeDefaultColumns = [key];
        else runtimeDefaultColumns.push(key);

        if (!column.data.runtimeDefault) {
          const {
            data: { default: def, encode },
          } = column;

          column.data.runtimeDefault = encode
            ? () => encode(def())
            : (def as () => unknown);
        }
      }

      if (
        column.data.setOnCreate ||
        column.data.setOnUpdate ||
        column.data.setOnSave
      ) {
        hasHookSetters = true;
      }
    }

    this.internal = {
      runtimeDefaultColumns,
      asyncStorage,
      scopes,
      snakeCase: options.snakeCase,
      noPrimaryKey: options.noPrimaryKey === 'ignore',
      comment: options.comment,
      nowSQL: options.nowSQL,
      tableData,
      selectAllCount,
    } as QueryInternal;

    this.q = {
      adapter: adapterNotInTransaction,
      shape: shape as Column.QueryColumnsInit,
      handleResult,
      logger,
      log: logParamToLogObject(logger, options.log),
      autoPreparedStatements: options.autoPreparedStatements ?? false,
      defaultParsers: hasParsers ? parsers : undefined,
      language: options.language,
      schema: options?.schema,
    } as QueryData;

    let shapeHasPrimaryKey: boolean | undefined;
    for (const key in shape) {
      if (shape[key].data.primaryKey) {
        shapeHasPrimaryKey = true;

        if (this.internal.singlePrimaryKey) {
          this.internal.singlePrimaryKey = undefined as never;
          break;
        }

        this.internal.singlePrimaryKey = key as never;
      }
    }

    if (
      !shapeHasPrimaryKey &&
      !tableData.primaryKey &&
      shape !== anyShape &&
      options.noPrimaryKey !== 'ignore'
    ) {
      const message = `Table ${table} has no primary key`;
      if (options.noPrimaryKey === 'error') throw new Error(message);
      else logger.warn(message);
    }

    const columns = Object.keys(shape) as unknown as (keyof Shape)[];

    this.columns = columns as (keyof Shape)[];

    if (options.computed) {
      applyComputedColumns(this, options.computed);
    }

    if (prepareSelectAll) {
      const selectAllShape: RecordUnknown = (this.q.selectAllShape = {});
      const list: string[] = [];
      for (const key in shape) {
        const column = shape[key] as unknown as Column;
        if (!column.data.explicitSelect) {
          list.push(
            column.data.name ? `"${column.data.name}" "${key}"` : `"${key}"`,
          );
          selectAllShape[key] = column;
        }
      }
      this.q.selectAllColumns = list;
    } else {
      this.q.selectAllShape = { ...shape };
    }

    if (modifyQuery) {
      for (const cb of modifyQuery) {
        cb(this);
      }
    }

    this.error = class extends QueryError {
      constructor(message?: string) {
        super(self, message);
      }
    };

    if (options.scopes) {
      for (const key in options.scopes) {
        const scopeFn = options.scopes[key];

        (scopes as RecordUnknown)[key] = (q: Query) => {
          const result = scopeFn(q as never) as Query;
          return { and: result.q.and, or: result.q.or };
        };
      }

      if (scopes.default) {
        this.q.scopes = { default: scopes.default };
      }
    }

    if (softDelete) {
      enableSoftDelete(this, table, shape, softDelete, scopes);
    }

    if (hasHookSetters) {
      const hooks = {
        setOnCreate: _queryHookBeforeCreate,
        setOnUpdate: _queryHookBeforeUpdate,
        setOnSave: _queryHookBeforeSave,
      };

      for (const key in shape) {
        const { data } = shape[key] as unknown as Column;

        for (const hookKey in hooks) {
          const fn = data[hookKey as 'setOnCreate'];
          if (fn) {
            hooks[hookKey as 'setOnCreate'](this, (arg) => {
              const value = fn(arg);
              if (value !== undefined) {
                arg.set({ [key]: value });
              }
            });
          }
        }
      }
    }
  }

  /**
   * When in transaction, returns a db adapter object for the transaction,
   * returns a default adapter object otherwise.
   */
  $getAdapter(): Adapter {
    const store = this.internal.asyncStorage.getStore();
    return (store?.transactionAdapter ??
      this.adapterNotInTransaction) as Adapter;
  }

  [inspect.custom]() {
    return `Query<${this.table}>`;
  }

  /**
   * Use `query` to perform raw SQL queries.
   *
   * ```ts
   * const value = 1;
   *
   * // it is safe to interpolate inside the backticks (``):
   * const result = await db.query<{ one: number }>`SELECT ${value} AS one`;
   * // data is inside `rows` array:
   * result.rows[0].one;
   * ```
   *
   * If the query is executing inside a transaction, it will use the transaction connection automatically.
   *
   * ```ts
   * await db.transaction(async () => {
   *   // both queries will execute in the same transaction
   *   await db.query`SELECT 1`;
   *   await db.query`SELECT 2`;
   * });
   * ```
   *
   * Alternatively, support a simple SQL string, with optional `values`:
   *
   * Note that the values is a simple array, and the SQL is referring to the values with `$1`, `$2` and so on.
   *
   * ```ts
   * const value = 1;
   *
   * // it is NOT safe to interpolate inside a simple string, use `values` to pass the values.
   * import { raw } from 'orchid-orm';
   *
   * const result = await db.query<{ one: number }>(raw({
   *   raw: 'SELECT $1 AS one',
   *   values: [value],
   * }));
   * // data is inside `rows` array:
   * result.rows[0].one;
   * ```
   *
   * @param args - SQL template literal, or a raw SQL object created by `raw()` or `sql()` function
   */
  get query(): DbSqlQuery {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const q = this;
    let query = q._query;
    if (!query) {
      q._query = query = Object.assign(
        (...args: SQLQueryArgs) => performQuery(q, args, 'query'),
        {
          async records(...args: SQLQueryArgs) {
            const { rows } = await performQuery(q, args, 'query');
            return rows;
          },
          async take(...args: SQLQueryArgs) {
            const {
              rows: [row],
            } = await performQuery(q, args, 'query');
            if (!row) throw new NotFoundError(q);
            return row;
          },
          async takeOptional(...args: SQLQueryArgs) {
            const { rows } = await performQuery(q, args, 'query');
            return rows[0];
          },
          async rows(...args: SQLQueryArgs) {
            const { rows } = await performQuery(q, args, 'arrays');
            return rows;
          },
          async pluck(...args: SQLQueryArgs) {
            const { rows } = await performQuery(q, args, 'arrays');
            return rows.map((row) => row[0]);
          },
          async get(...args: SQLQueryArgs) {
            const {
              rows: [row],
            } = await performQuery(q, args, 'arrays');
            if (!row) throw new NotFoundError(q);
            return row[0];
          },
          async getOptional(...args: SQLQueryArgs) {
            const { rows } = await performQuery(q, args, 'arrays');
            return rows[0]?.[0];
          },
        },
      ) as never;
    }
    return query;
  }
  private _query?: DbSqlQuery;

  /**
   * Performs a SQL query, returns a db result with array of arrays instead of objects:
   *
   * ```ts
   * const value = 1;
   *
   * // it is safe to interpolate inside the backticks (``):
   * const result = await db.queryArrays<[number]>`SELECT ${value} AS one`;
   * // `rows` is an array of arrays:
   * const row = result.rows[0];
   * row[0]; // our value
   * ```
   *
   * @param args - SQL template literal, or a raw SQL object created by `raw()` or `sql()` function
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  queryArrays<R extends any[] = any[]>(
    ...args: SQLQueryArgs
  ): Promise<QueryArraysResult<R>> {
    return performQuery<QueryArraysResult<R>>(this, args, 'arrays');
  }
}

setDb(Db);

applyMixins(Db, [QueryMethods]);
Db.prototype.constructor = Db;
Db.prototype.__isQuery = true;

// Function to build a new table instance.
export interface DbTableConstructor<ColumnTypes> {
  <
    Table extends string,
    Shape extends Column.QueryColumnsInit,
    Data extends MaybeArray<TableDataItem>,
    Options extends DbTableOptions<ColumnTypes, Table, Shape>,
  >(
    table: Table,
    shape?: ((t: ColumnTypes) => Shape) | Shape,
    tableData?: TableDataFn<Shape, Data>,
    options?: Options,
  ): Db<
    Table,
    Shape,
    keyof ShapeColumnPrimaryKeys<Shape> extends never
      ? never
      : ShapeColumnPrimaryKeys<Shape>,
    ShapeUniqueColumns<Shape> | TableDataItemsUniqueColumns<Shape, Data>,
    TableDataItemsUniqueColumnTuples<Shape, Data>,
    UniqueConstraints<Shape> | TableDataItemsUniqueConstraints<Data>,
    ColumnTypes,
    Shape & ComputedColumnsFromOptions<Options['computed']>,
    MapTableScopesOption<Options>,
    ColumnsShape.DefaultSelectKeys<Shape>
  > & {
    ko: Shape;
  };
}

export interface DbSqlMethod<ColumnTypes> {
  <T>(
    ...args: StaticSQLArgs
  ): RawSql<Column.Pick.QueryColumnOfType<T>, ColumnTypes>;
  <T>(
    ...args: [DynamicSQLArg<Column.Pick.QueryColumnOfType<T>>]
  ): DynamicRawSQL<Column.Pick.QueryColumnOfType<T>, ColumnTypes>;
  ref(name: string): SqlRefExpression;
  unsafe(sql: string | number | boolean): UnsafeSqlExpression;
}

export type MapTableScopesOption<T> = T extends { scopes: RecordUnknown }
  ? T extends { softDelete: true | PropertyKey }
    ? T['scopes'] & NonDeletedScope
    : T['scopes']
  : T extends { softDelete: true | PropertyKey }
    ? { nonDeleted: unknown }
    : EmptyObject;

export interface DbResult<ColumnTypes>
  extends
    Db<string, never, never, never, never, never, ColumnTypes>,
    DbTableConstructor<ColumnTypes> {
  adapterNotInTransaction: Adapter;
  adapter: Adapter;
  close: Adapter['close'];
  sql: DbSqlMethod<ColumnTypes>;
}
{
}

/**
 * If you'd like to use the query builder of OrchidORM as a standalone tool, install `pqb` package and use `createDb` to initialize it.
 *
 * As `Orchid ORM` focuses on ORM usage, docs examples mostly demonstrates how to work with ORM-defined tables,
 * but everything that's not related to table relations should also work with `pqb` query builder on its own.
 *
 * It is accepting the same options as `orchidORM` + options of `createBaseTable`:
 *
 * ```ts
 * import { createDb } from 'orchid-orm';
 *
 * import { zodSchemaConfig } from 'orchid-orm-schema-to-zod';
 * // or
 * import { SchemaConfig } from 'orchid-orm-valibot';
 *
 * const db = createDb({
 *   // db connection options
 *   databaseURL: process.env.DATABASE_URL,
 *   log: true,
 *
 *   // columns in db are in snake case:
 *   snakeCase: true,
 *
 *   // override default SQL for timestamp, see `nowSQL` above
 *   nowSQL: `now() AT TIME ZONE 'UTC'`,
 *
 *   // optional, but recommended: makes zod schemas for your tables
 *   schemaConfig: zodSchemaConfig,
 *   // or
 *   schemaConfig: valibotSchemaConfig,
 *
 *   // override column types:
 *   columnTypes: (t) => ({
 *     // by default timestamp is returned as a string, override to a number
 *     timestamp: () => t.timestamp().asNumber(),
 *   }),
 * });
 * ```
 *
 * After `db` is defined, construct queryable tables in such way:
 *
 * ```ts
 * export const User = db('user', (t) => ({
 *   id: t.identity().primaryKey(),
 *   name: t.string(),
 *   password: t.varchar(100),
 *   age: t.integer().nullable(),
 *   ...t.timestamps(),
 * }));
 * ```
 *
 * Now the `User` can be used for making type-safe queries:
 *
 * ```ts
 * const users = await User.select('id', 'name') // only known columns are allowed
 *   .where({ age: { gte: 20 } }) // gte is available only on the numeric field, and the only number is allowed
 *   .order({ createdAt: 'DESC' }) // type safe as well
 *   .limit(10);
 *
 * // users array has a proper type of Array<{ id: number, name: string }>
 * ```
 *
 * The optional third argument is for table options:
 *
 * ```ts
 * const Table = db('table', (t) => ({ ...columns }), {
 *   // provide this value if the table belongs to a specific database schema:
 *   schema: 'customTableSchema',
 *   // override `log` option of `createDb`:
 *   log: true, // boolean or object described `createdDb` section
 *   logger: { ... }, // override logger
 *   noPrimaryKey: 'ignore', // override noPrimaryKey
 *   snakeCase: true, // override snakeCase
 * })
 * ```
 */
export const createDbWithAdapter = <
  SchemaConfig extends ColumnSchemaConfig = DefaultSchemaConfig,
  ColumnTypes = DefaultColumnTypes<SchemaConfig>,
>({
  log,
  logger,
  snakeCase,
  schemaConfig = defaultSchemaConfig as unknown as SchemaConfig,
  columnTypes: ctOrFn = makeColumnTypes(schemaConfig) as unknown as ColumnTypes,
  schema,
  ...options
}: DbOptionsWithAdapter<SchemaConfig, ColumnTypes>): DbResult<ColumnTypes> => {
  const { adapter } = options;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const commonOptions: DbTableOptions<any, any, Column.QueryColumns> = {
    log,
    logger,
    autoPreparedStatements: options.autoPreparedStatements ?? false,
    noPrimaryKey: options.noPrimaryKey ?? 'error',
    nowSQL: options.nowSQL,
    snakeCase,
  };

  const ct =
    typeof ctOrFn === 'function'
      ? (
          ctOrFn as unknown as (
            t: DefaultColumnTypes<SchemaConfig>,
          ) => ColumnTypes
        )(makeColumnTypes(schemaConfig))
      : ctOrFn;

  const asyncStorage = new AsyncLocalStorage<AsyncState>();

  const qb = _initQueryBuilder(
    adapter,
    ct,
    asyncStorage,
    commonOptions,
    options,
  );

  commonOptions.schema = schema;

  const { nowSQL } = options;
  const tableConstructor: DbTableConstructor<ColumnTypes> = (
    table,
    shape,
    dataFn,
    options,
  ) => {
    return new Db(
      adapter as Adapter,
      qb as never,
      table,
      typeof shape === 'function'
        ? getColumnTypes(ct, shape, nowSQL, options?.language)
        : shape || (anyShape as never),
      ct,
      asyncStorage,
      { ...commonOptions, ...options },
      parseTableData(dataFn),
    ) as never;
  };

  const db = Object.assign(tableConstructor, qb, {
    adapter,
    close: () => adapter.close(),
  });

  Object.setPrototypeOf(db, Db.prototype);

  db.sql = _createDbSqlMethod(ct) as typeof db.sql;

  return db as never;
};

export function _createDbSqlMethod<ColumnTypes>(
  columnTypes: ColumnTypes,
): DbSqlMethod<ColumnTypes> {
  // bind column types
  const fn = ((...args: unknown[]) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sql = (raw as any)(...args);
    sql.columnTypes = columnTypes;
    return sql;
  }) as DbSqlMethod<ColumnTypes>;
  fn.ref = sqlFn.ref;
  fn.unsafe = sqlFn.unsafe;
  return fn;
}

export const _initQueryBuilder = (
  adapter: Adapter,
  columnTypes: unknown,
  asyncStorage: AsyncLocalStorage<AsyncState>,
  commonOptions: DbTableOptions<unknown, undefined, Column.QueryColumns>,
  options: DbSharedOptions,
): Db => {
  const qb = new Db(
    adapter as Adapter,
    undefined as unknown as QueryBuilder,
    undefined,
    anyShape,
    columnTypes,
    asyncStorage,
    commonOptions,
  );

  qb.internal.nestedCreateBatchMax = options.nestedCreateBatchMax ?? 100;

  if (options.extensions) {
    const arr: DbExtension[] = [];
    for (const x of options.extensions) {
      if (typeof x === 'string') {
        arr.push({ name: x });
      } else {
        for (const key in x) {
          arr.push({ name: key, version: x[key] });
        }
      }
    }
    qb.internal.extensions = arr;
  }

  qb.internal.domains = options.domains;
  qb.internal.generatorIgnore = options.generatorIgnore;
  qb.internal.roles = options.roles;
  qb.internal.managedRolesSql = options.managedRolesSql;

  return (qb.qb = qb as never);
};
